import { useState, useRef, useEffect, useCallback, MouseEvent } from "react";
import videojs from "video.js";
import { useRecoilValue, useRecoilState, useSetRecoilState } from "recoil";
import { useInView } from "react-intersection-observer";

import { EmbeddablePlayerViewModel } from "./ViewModel";
import {
  buildIframeOptions,
  getActiveClipIndex,
  isAdPartner,
  isFaceItExpiredClip,
} from "./utils";
import { EmbeddablePlayerWrapper, AdClickOverlay } from "./styledComponents";
import { Loading } from "./Components/Loading";
import { Pruned } from "./Components/Pruned";
import { Overlay } from "./Components/Overlay";
import { Pending } from "./Components/Pending";
import { Error } from "./Components/Error";
import {
  ActivePlaylistClipState,
  PlaylistClipsState,
  WatchedClipsCountState,
} from "MVVM/State/embeddablePlaylist";
import {
  ActiveOverlayPopUpState,
  PlatformState,
  ShouldDisplayOverlayState,
  TrackedFirstHoverState,
} from "MVVM/State/iframe";
import { VideoPlayer } from "MVVM/VideoPlayer";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { countClipImpression } from "MVVM/Utilities/Clip";
import { formatPlaylistClip } from "MVVM/Views/EmbeddablePlaylist/Utilities";
import { TUserRole } from "./@types";
import { isMobileOnly } from "react-device-detect";
import { usePlaywireAds } from "MVVM/Hooks/usePlaywireAds";
import { useAds } from "MVVM/Providers";
import { analytics } from "MVVM/Analytics";

interface IEmbeddablePlayerProps {
  isInMobileExperiment?: boolean;
  userRole?: TUserRole;
  className?: string;
  onPlayEndVariant?: "history" | "nextUp" | "nextUpPlaylist";
  hideNextUpThumbnail?: boolean;
}

const EmbeddablePlayer = ({
  userRole = "creator",
  className,
  onPlayEndVariant = "history",
  isInMobileExperiment = false,
  hideNextUpThumbnail = false,
}: IEmbeddablePlayerProps) => {
  const videoPlayerRef = useRef<videojs.Player | null>(null);
  const { ref: inViewRef, inView } = useInView({ threshold: 0.5 });
  const [trackedOverlayDisplayed, setTrackedOverlayDisplayed] = useState(false);
  const [trackedFirstHover, setTrackedFirstHover] = useRecoilState(
    TrackedFirstHoverState,
  );
  const [shouldDisplayOverlay, setShouldDisplayOverlay] = useRecoilState(
    ShouldDisplayOverlayState,
  );
  const [hasAutoDisplayedHistoryPopup, setHasAutoDisplayedHistoryPopup] =
    useState(false);
  const [autoplayState, setAutoplayState] = useState(false);
  const [mutedState, setMutedState] = useState(false);
  const [mouseInactivityTimer, setMouseInactivityTimer] =
    useState<NodeJS.Timeout | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [triggeredPreRollAd, setTriggeredPreRollAd] = useState(false);
  const [videosWatchedCount, setVideosWatchedCount] = useState(0);
  const videosWatchedCountRef = useRef(videosWatchedCount);
  const platform = useRecoilValue(PlatformState);

  useEffect(() => {
    videosWatchedCountRef.current = videosWatchedCount;
  }, [videosWatchedCount]);

  const options = buildIframeOptions();
  const { data, loading, error, refetchClip } = EmbeddablePlayerViewModel();
  const [activeOverlayPopUp, setActiveOverlayPopUp] = useRecoilState(
    ActiveOverlayPopUpState,
  );

  const activePlaylistClip = useRecoilValue(ActivePlaylistClipState);
  const playlistClipsValue = useRecoilValue(PlaylistClipsState);
  const setActivePlaylistClip = useSetRecoilState(ActivePlaylistClipState);
  const setWatchedClipsCount = useSetRecoilState(WatchedClipsCountState);
  const {
    config: {
      display: { enabled: displayAdsEnabled },
    },
  } = useAds();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const background = params.get("background");
    const muted = params.get("muted");
    const shouldMute =
      muted === "false" && !autoplayState ? false : autoplayState;
    const autoplay = params.get("autoplay");
    const shouldAutoplay =
      [autoplay, background].includes("true") || autoplay !== "false";

    setAutoplayState(shouldAutoplay);
    setMutedState(shouldMute);
  }, [autoplayState, platform]);

  useEffect(() => {
    if (!videoPlayerRef.current || isAdPartner(platform)) return;

    if (inView) {
      videoPlayerRef.current.play();
      setAutoplayState(true);
    } else {
      videoPlayerRef.current.pause();
    }
  }, [inView]);

  useEffect(() => {
    if (!inView) return;

    setShouldDisplayOverlay(true);

    const timeout = setTimeout(() => {
      setShouldDisplayOverlay(false);
    }, 2000);

    return () => clearTimeout(timeout);
  }, [activePlaylistClip, inView, setShouldDisplayOverlay]);

  useEffect(() => {
    if (isMobileOnly && isVideoPlaying) {
      setShouldDisplayOverlay(false);
    }
  }, [isVideoPlaying, setShouldDisplayOverlay]);

  const resetMouseInactivityTimer = useCallback(() => {
    setMouseInactivityTimer((prevTimer) => {
      if (prevTimer) {
        clearTimeout(prevTimer);
      }

      return setTimeout(() => {
        if (isVideoPlaying && !isMobileOnly) {
          setShouldDisplayOverlay(false);
        }
      }, 2000);
    });
  }, [isVideoPlaying, setShouldDisplayOverlay]);

  useEffect(() => {
    return () => {
      if (mouseInactivityTimer) {
        clearTimeout(mouseInactivityTimer);
      }
    };
  }, [mouseInactivityTimer]);

  const { track, analyticsProperties } = useAnalyticsContext({
    clip: data?.clip,
  });

  useEffect(() => {
    if (window.ReportingObserver) {
      const observer = new window.ReportingObserver((reports) => {
        reports.forEach((report) => {
          if (
            report.type === "intervention" &&
            report.body &&
            report.body.id === "HeavyAdIntervention"
          ) {
            track("Heavy Ad Intervention", {
              reason: report.body.message || "Heavy ad intervention",
              clipId: data?.clip?.internalId,
            });
          }
        });
      });
      observer.observe();

      return () => observer.disconnect();
    }
  }, [track, data?.clip?.internalId]);

  useEffect(() => {
    setHasAutoDisplayedHistoryPopup(false);
    setTrackedOverlayDisplayed(false);
    setVideosWatchedCount(0);
  }, [data?.clip?.internalId]);

  useEffect(() => {
    if (shouldDisplayOverlay && !trackedOverlayDisplayed) {
      track("Embeddable Player Overlay Displayed");
      setTrackedOverlayDisplayed(true);
    }
  }, [shouldDisplayOverlay, trackedOverlayDisplayed, track]);

  useEffect(() => {
    if (isAdPartner(platform) && !triggeredPreRollAd) {
      track("Component Viewed", {
        locationIdentifier: "ad_click_overlay",
        location: "embeddable_player",
      });
    }
  }, [platform, triggeredPreRollAd, track]);

  const playNextVideo = useCallback(() => {
    setShouldDisplayOverlay(true);
    setIsVideoPlaying(false);
    resetMouseInactivityTimer();
    if (onPlayEndVariant) setActiveOverlayPopUp(onPlayEndVariant);
  }, [
    onPlayEndVariant,
    setShouldDisplayOverlay,
    setActiveOverlayPopUp,
    resetMouseInactivityTimer,
  ]);

  const advanceToNextClip = useCallback(() => {
    const playlistClips = playlistClipsValue;
    const currentClip = activePlaylistClip;
    if (!playlistClips || !currentClip) return;

    const index = getActiveClipIndex(playlistClips, currentClip);

    const nextClip =
      index >= 0 && index < playlistClips.length - 1
        ? playlistClips[index + 1]
        : playlistClips[0];

    if (nextClip) {
      const formattedClip = formatPlaylistClip(nextClip);
      setActivePlaylistClip(formattedClip);
      setWatchedClipsCount((prev) => prev + 1);
    }
  }, [
    playlistClipsValue,
    activePlaylistClip,
    setActivePlaylistClip,
    setWatchedClipsCount,
  ]);
  const handlePlaylistAdvance = useCallback(() => {
    const playlistClips = playlistClipsValue;
    const currentClip = activePlaylistClip;

    if (playlistClips && currentClip) {
      advanceToNextClip();
    } else {
      playNextVideo();
    }
  }, [
    playlistClipsValue,
    activePlaylistClip,
    advanceToNextClip,
    playNextVideo,
  ]);

  const { activeAd, triggerAd } = usePlaywireAds({
    onComplete: () => {
      if (videosWatchedCountRef.current === 0) {
        setTriggeredPreRollAd(true);
        return videoPlayerRef.current?.play();
      }

      handlePlaylistAdvance();
    },
    onShowHiddenContainer: () => {
      if (videosWatchedCountRef.current === 0) {
        setTriggeredPreRollAd(true);
        return videoPlayerRef.current?.play();
      }

      handlePlaylistAdvance();
    },
    onAdStarted: () => {
      setTriggeredPreRollAd(true);
    },
    path: "iframe",
  });

  const handleAdOverlayEvent = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    analytics.track("Ad Overlay Clicked", {
      locationIdentifier: "ad_overlay",
      location: "embeddable_player_overlay",
    });
    triggerAd();
  };

  const handlePlayerReady = (player?: videojs.Player | null) => {
    if (player) {
      player.ready(() => {
        const controlBar = player.controlBar.el();

        if (!controlBar.querySelector(".vjs-control-bar-brand")) {
          const img = document.createElement("img");

          img.onclick = () => {
            const utmSource =
              data?.clip?.developer?.slug ||
              new URLSearchParams(window.location.search).get("platform") ||
              "unknown";

            const ajs_aid = window.rudderanalytics.getAnonymousId();
            track("Button - Click", {
              locationIdentifier: "allstar_logo",
              location: "embeddable_player_overlay",
            });
            window.open(
              `${window.location.origin}?utm_source=${utmSource}&ajs_aid=${ajs_aid}`,
              "_blank",
            );
          };

          const imgSrc = `${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/iframe/allstargg.png`;
          img.src = imgSrc;
          img.className = "vjs-control-bar-brand";

          controlBar.appendChild(img);
        }
      });
    }
  };

  if (loading) return <Loading />;

  const clip = data?.clip;

  const clipPartnerDoesNotMatchPlatform =
    clip?.developer?.slug && platform && clip?.developer?.slug !== platform;

  if (
    error ||
    !clip ||
    clipPartnerDoesNotMatchPlatform ||
    isFaceItExpiredClip({ clip, platform })
  )
    return <Error />;
  if (["PRUNED", "ON_DEMAND"].includes(clip.status))
    return (
      <Pruned
        thumbnail={clip.thumbnailStandardUrl}
        clip={clip}
        refetchClip={refetchClip}
      />
    );
  if (["PENDING", "ERROR"].includes(clip.status))
    return <Pending clip={clip} refetchClip={refetchClip} />;

  const handlePlayEnd = () => {
    setVideosWatchedCount((prev) => {
      const newCount = prev + 1;

      if (
        (newCount % 2 === 0 || newCount === 1) &&
        !activeAd &&
        isAdPartner(platform)
      ) {
        triggerAd();
      } else {
        playNextVideo();
      }
      return newCount;
    });
  };

  return (
    <EmbeddablePlayerWrapper
      ref={inViewRef}
      onMouseOver={() => {
        setShouldDisplayOverlay(true);
        resetMouseInactivityTimer();
        const searchParams = new URLSearchParams(window.location.search);
        const playlistParam = searchParams.get("playlist");

        if (!trackedFirstHover && !playlistParam) {
          track("Iframe First Hover", {
            locationIdentifier: "iframe_first_hover",
            location: "embeddable_player",
            platform,
          });
          setTrackedFirstHover(true);
        }
      }}
      onMouseMove={() => {
        setShouldDisplayOverlay(true);
        resetMouseInactivityTimer();
      }}
      onTouchStart={() => {
        resetMouseInactivityTimer();
      }}
      onMouseLeave={() => {
        setShouldDisplayOverlay(false);
        if (mouseInactivityTimer) {
          clearTimeout(mouseInactivityTimer);
        }
      }}
      className={className}
      id="video_wrapper"
    >
      {isAdPartner(platform) && !triggeredPreRollAd && (
        <AdClickOverlay onClick={handleAdOverlayEvent} />
      )}
      {!displayAdsEnabled && (
        <Overlay
          player={videoPlayerRef.current}
          clip={clip}
          userRole={userRole}
          hideNextUpThumbnail={hideNextUpThumbnail}
        />
      )}
      <VideoPlayer
        key={clip?.internalId}
        watchAnalyticsProperties={analyticsProperties}
        playAnalyticsProperties={analyticsProperties}
        mp4Source={
          isInMobileExperiment && clip.portraitUrl ? clip.portraitUrl : clip.url
        }
        refForVideoPlayer={videoPlayerRef}
        wrapperId={"video_wrapper"}
        options={{
          ...options,
          autoplay: autoplayState && !isAdPartner(platform),
          muted: mutedState ? mutedState : true,
          poster:
            options.useOpenGraphImage && clip.thumbnailOverlayUrl
              ? clip.thumbnailOverlayUrl
              : clip.thumbnailStandardUrl,
          inactivityTimeout: isMobileOnly ? 0 : 2000,
        }}
        onTouchStartCapture={(event) => {
          const target = event.target as HTMLElement;
          const controlBar = target.closest(".vjs-control-bar");

          if (controlBar) {
            return;
          }

          if (videoPlayerRef.current?.paused()) {
            if (isMobileOnly) {
              setShouldDisplayOverlay(false);
            }
            videoPlayerRef.current?.play();
          } else {
            videoPlayerRef.current?.pause();
          }
        }}
        loop={options.shouldLoop}
        onPlayPause={() => {
          setShouldDisplayOverlay(true);
          setIsVideoPlaying((prev) => !prev);
          resetMouseInactivityTimer();
          if (!hasAutoDisplayedHistoryPopup && !activeOverlayPopUp) {
            setActiveOverlayPopUp("history");
            setHasAutoDisplayedHistoryPopup(true);
          }
        }}
        onPlayEnd={handlePlayEnd}
        onPlayStart={() => {
          setIsVideoPlaying(true);
          countClipImpression(clip.shareId);
        }}
        onPlay={() => {
          setIsVideoPlaying(true);
          setActiveOverlayPopUp(null);
        }}
        onReady={handlePlayerReady}
        className="embeddable-player"
        volumeStorage={"local"}
      />
    </EmbeddablePlayerWrapper>
  );
};

export default EmbeddablePlayer;
